import { Typography, TypographyVariant } from '../Typography';
import { ComponentPropsWithoutRef, Ref, useImperativeHandle, useRef, useState } from 'react';
import clsx from 'clsx';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export interface InputFieldRef {
  setCompact: (_compact: boolean) => void;
  focusInput: () => void;
}

export interface InputFieldProps extends ComponentPropsWithoutRef<'input'> {
  id: string;
  fieldsetClassName?: string;
  containerClassName?: string;
  label?: string;

  onWhite?: boolean;
  endAdornment?: React.ReactNode;
  endActionComponent?: React.ReactNode;

  status?: 'error' | 'success';
  completed?: boolean;
  defaultCompact?: boolean;

  /**  */
  supportText?: string;
  maxCharCount?: number;
  ref?: Ref<InputFieldRef>;
}

const disabledVariant = [
  `text-[--inputfield-input-text-disabled]`,
  `bg-[--inputfield-input-bg-disabled]`,
  `hover:border-[transparent]`,
  `focus-within:border-[transparent]`,
  `!border-[transparent]`,
];

const InputContainerCVA = cva(
  [
    'flex',
    'h-[48px]',
    'gap-[--padding-zero]',
    `p-[--padding-md]`,
    `rounded-[--corner-xs]`,
    `border-[1px]`,
    `border-solid`,
    `border-[transparent]`,
    `hover:border-[--inputfield-input-border-active]`,
    `focus-within:border-[--inputfield-input-border-active]`,
    'font-primary',
    TypographyVariant.paragraph020,
  ],
  {
    variants: {
      onWhite: {
        true: 'bg-[--inputfield-input-bg-default]',
        false: 'bg-[--inputfield-input-bg-white]',
      },
      completed: {
        true: `!border-[--inputfield-input-border-active]`,
        false: '',
      },
      status: {
        error: '!border-[--inputfield-input-border-error]',
        success: '!border-[--inputfield-input-border-success]',
      },
      disabled: {
        true: clsx(disabledVariant),
        false: '',
      },
    },
    compoundVariants: [
      {
        disabled: true,
        status: 'error',
        className: clsx(disabledVariant),
      },
      {
        disabled: true,
        status: 'success',
        className: clsx(disabledVariant),
      },
    ],
  }
);

const InputCVA = cva([], {
  variants: {
    compact: {
      true: 'flex-0 w-0',
      false: 'flex-1',
    },
  },
});

export const InputField = ({
  containerClassName,
  fieldsetClassName,
  label,
  className,
  id,
  supportText,
  maxCharCount,
  onWhite = true,
  endAdornment,
  endActionComponent,
  value,
  disabled,
  completed,
  status,
  onChange,
  defaultCompact = false,
  ref,
  ...otherProps
}: InputFieldProps) => {
  const [compact, setCompact] = useState(defaultCompact);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => {
    return {
      setCompact: setCompact,
      focusInput: () => inputRef.current?.focus(),
    };
  }, []);

  const hasSupportText = supportText !== undefined;
  const hasCharCount = maxCharCount !== undefined;
  const hasSupportLabel = hasSupportText || hasCharCount;
  const charCount = value ? String(value).length : 0;

  return (
    <fieldset
      className={twMerge(
        'flex flex-col rounded-[--corner-xs] gap-[--padding-xs]',
        fieldsetClassName
      )}
    >
      {label ? (
        <Typography
          as="label"
          htmlFor={id}
          variant={'paragraph010'}
          className="text-[--inputfield-label-color]"
        >
          {label}
        </Typography>
      ) : null}
      <div className="flex gap-[--padding-sm]">
        <div
          className={twMerge(
            'flex items-center gap-[--spacing-md] flex-0',
            InputContainerCVA({
              onWhite,
              disabled,
              completed,
              status,
            }),
            containerClassName
          )}
        >
          <input
            ref={inputRef}
            id={id}
            className={twMerge(
              ['flex', 'outline-none', 'bg-[transparent]'],
              InputCVA({ compact }),
              className
            )}
            value={value}
            onChange={onChange}
            disabled={disabled || completed}
            {...otherProps}
          />
          {endAdornment ? endAdornment : null}
        </div>
        {endActionComponent ? endActionComponent : null}
      </div>
      {hasSupportLabel ? (
        <div className="flex [&>*:last-child]:text-right [&>*:first-child]:self-start">
          {hasSupportText ? (
            <Typography className="flex-1 text-[--inputfield-label-color]" as="span">
              {supportText}
            </Typography>
          ) : null}
          {hasCharCount ? (
            <Typography
              className="flex-1 text-[--inputfield-label-color]"
              as="span"
            >{`${charCount}/${maxCharCount}`}</Typography>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
};
